/**
 * client/src/components/forensic-recovery-dialog-copy.ts
 *
 * Pure copy/helper constants for `forensic-recovery-dialog.tsx`, split out
 * to keep that file under the 300-line cap (DEVELOPMENT/code-style.md).
 * No JSX, no hooks -- just the method-chooser copy and step-label helper.
 */
import type { ForensicRecoveryPath } from "@shared/forensic-recovery-status";

export function isPotentiallyGatedPath(path: ForensicRecoveryPath): boolean {
  return path === "guardian_quorum" || path === "key_guardian";
}

export const METHOD_COPY = {
  guardian_quorum: {
    title: "Trusted people",
    // F10 (adversarial review round 2): "recovery request" is the RECOVERY
    // ceremony's own verb (2-of-3), while this dialog's count here is the
    // FREEZE-LIFT ceremony's (3-of-3) — sharing both the noun ("Trusted
    // people", the Recovery page's own method title) and a recovery-shaped
    // verb while stating a different number reads as the same claim twice,
    // with two different thresholds. Uses the freeze-lift verb instead.
    why: "Three trusted people approve to lift the pause.",
    action: "Ask trusted people",
  },
  key_quorum: {
    title: "Security devices",
    why: "Use two security devices prepared before this incident.",
    action: "Use security devices",
  },
  key_guardian: {
    title: "Security device and trusted person",
    why: "Combine one prepared security device with one trusted person's approval.",
    action: "Use device and trusted person",
  },
  key_offline_code: {
    title: "Security device and offline code",
    why: "Combine one prepared security device with your saved offline code.",
    action: "Use device and offline code",
  },
} satisfies Record<ForensicRecoveryPath, { title: string; why: string; action: string }>;

export const TIME_COPY = {
  immediate: "As soon as all approvals arrive",
  "24_hours": "At least 24 hours after the checks finish",
  "72_hours": "At least 72 hours after the checks finish",
} as const;

export function stepLabel(key: "security_device" | "trusted_person" | "offline_code", index: number): string {
  if (key === "trusted_person") return `Trusted person's approval${index > 0 ? ` ${index + 1}` : ""}`;
  if (key === "offline_code") return "Offline code";
  return index > 0 ? "Second security device" : "First security device";
}
